import React, { FC, useEffect, memo, useRef } from 'react';
import classNames from 'classnames';

import { TEXTS } from 'application/pages/texts';
import { HELP_CENTER_CONTRACTS_LINK } from 'application/pages/constants';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import CustomInfo from 'application/pages/StreamPage/renderer/CustomInfo/CustomInfo';
import SelectableList from 'application/components/SelectableList';
import EmptyState from 'application/components/EmptyState';
import PointItemContent from 'application/components/PointItemContent';
import { useLocation, useParams } from 'application/providers/RouterProvider';
import { LocationStatePointsType, StreamGroupsCollectionPageParamsType } from 'application/pages/types';
import { useOnboardingActionContext, useOnboardingContext } from 'application/components/common';
import { SelectPlayerFlowType } from 'application/components/common/SelectPlayerFlow/types';

import { PointShortTypeFragment } from '@shared/api/graphql/generated';

import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import InputSearch from '@zvuk-b2b/react-uikit/ui/InputSearch';
import Checkbox, { LabelPlacement } from '@zvuk-b2b/react-uikit/ui/Checkbox';
import Info from '@zvuk-b2b/react-uikit/ui/Info';

import useStreamBindModalController from './useStreamBindModalController';
import useStreamBindModalViewModel from './useStreamBindModalViewModel';
import { ActionButtons } from './components';

import styles from './index.module.scss';

const StreamBindModal: FC<ModalProps[ModalTypes.STREAM_BIND]> = ({ close, openModal, replaceModal }) => {
  const pathParams = useParams<StreamGroupsCollectionPageParamsType>();
  const location = useLocation<LocationStatePointsType>();
  const [pointFromLocation] = location.state?.pointsFromLocation || [];
  const { isIpad } = useMedia();
  const { inFlow, flowType, isTemplateMode } = useOnboardingContext();
  const { clearCurrentPoint } = useOnboardingActionContext();
  const inputSearchRef = useRef<HTMLInputElement>(null);

  const targetStreamId = pathParams.id;

  const {
    streamSetPointsState,
    isEmpty,
    isEmptyFiltered,
    points,
    pointsLoading,
    isCustomInfoShouldShow,
    paginationInfo,
    allPointsCount,
    isLoading,
    userOnboardingFeaturesLoading,
    isAcceptanceAdvancedPending,
    streamSetToPointsLoading,
    onFilterExecute,
    loadMorePoints,
    streamSetToPoints,
    copyTemplateStream,
    getSavedStreams,
    getStream,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    readStreamWithThematic,
    clearStreamWithThematic,
    setPointInput,
  } = useStreamBindModalViewModel();

  const {
    selectedPoints,
    disabledPoints,
    onPointChange,
    searchText,
    isStreamBindButtonLoading,
    setIsStreamBindButtonLoading,
    onSearchChange,
    onInitialSearchChange,
  } = useStreamBindModalController({
    points,
    targetStreamId,
    pointFromLocation,
    onFilterExecute,
    setPointInput,
  });

  const pointIdsInInput =
    streamSetPointsState.filter && streamSetPointsState.filter.pointIds
      ? [...streamSetPointsState.filter.pointIds.keys()]
      : [];

  const isStreamBindButtonDisabled = !pointIdsInInput.find((pointId) => !disabledPoints.has(pointId));

  useEffect(() => {
    if (pointFromLocation?.pointAddress && pointFromLocation?.pointAddress.length !== 0) {
      onInitialSearchChange(pointFromLocation?.pointAddress);
    }
  }, [pointFromLocation]);

  useEffect(() => {
    if (!isLoading) {
      inputSearchRef.current?.focus();
    }
  }, [isLoading]);

  const onConfirmClose = () => {
    if (flowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      close();
      clearCurrentPoint();
      clearStreamWithThematic();
      return;
    }

    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
  };

  const handleCancel = () => {
    if (!pointFromLocation?.pointId) {
      onSearchChange('');
    }

    if (inFlow) {
      onConfirmClose();
    } else {
      clearStreamWithThematic();
      close();
    }
  };

  const handleChangeStream = async (streamId: string) => {
    await streamSetToPoints(streamId);
  };

  return (
    <ModalScreen
      title={TEXTS.MODAL_BIND_TITLE}
      titlePlacement="left"
      disableScroll={!isIpad}
      contentClassName={styles.content}
      onClose={handleCancel}
      actionButtons={
        <ActionButtons
          selectedPoints={streamSetPointsState.filter?.pointIds}
          isAcceptanceAdvancedPending={isAcceptanceAdvancedPending}
          isLoading={isLoading}
          isStreamBindButtonLoading={isStreamBindButtonLoading}
          isStreamBindButtonDisabled={isStreamBindButtonDisabled}
          streamSetToPointsLoading={streamSetToPointsLoading}
          replaceModal={replaceModal}
          setIsStreamBindButtonLoading={setIsStreamBindButtonLoading}
          handleChangeStream={handleChangeStream}
          copyTemplateStream={copyTemplateStream}
          getSavedStreams={getSavedStreams}
          getStream={getStream}
          onboardingPassedRequest={onboardingPassedRequest}
          refetchFeaturesOnboarding={refetchFeaturesOnboarding}
          readStreamWithThematic={readStreamWithThematic}
        />
      }>
      {isCustomInfoShouldShow && userOnboardingFeaturesLoading && <div className={styles.hintSkeleton} />}

      {isCustomInfoShouldShow && !userOnboardingFeaturesLoading && (
        <CustomInfo
          textObject={TEXTS.MODAL_INFO}
          link={HELP_CENTER_CONTRACTS_LINK}
        />
      )}

      <InputSearch
        ref={inputSearchRef}
        containerClassName={styles.searchInput}
        value={searchText}
        elementId="toolbar_input_search"
        onChange={onSearchChange}
        isLoading={isLoading || isStreamBindButtonLoading}
        placeholder={TEXTS.MODAL_BIND_SEARCH_PLACEHOLDER}
      />

      {!isTemplateMode && points.length !== 0 && points.length === disabledPoints.size && (
        <Info className={styles.info}>{TEXTS.MODAL_BIND_EQUAL_STREAMS_TEXT}</Info>
      )}

      {!isEmpty && !isEmptyFiltered && (
        <SelectableList<PointShortTypeFragment>
          elementId="checkbox_item_bind"
          listWrapperClassName={classNames(styles.listWrapper, {
            [styles.listWrapperLoading]: pointsLoading,
          })}
          items={points}
          itemKeyForRender="address"
          itemContent={
            <PointItemContent
              showStreamData
              isLoading={isLoading || isStreamBindButtonLoading}
              hintText={TEXTS.MODAL_BIND_DISABLED_POINT_HINT_TEXT}
              hintIconNearSelector
            />
          }
          labelPlacement={LabelPlacement.LEFT}
          itemsLoading={pointsLoading}
          isLoading={isLoading || isStreamBindButtonLoading}
          selectorDisabled={isLoading || isStreamBindButtonLoading}
          selectedItems={selectedPoints}
          disabledItems={disabledPoints}
          onChange={onPointChange}
          loadMore={loadMorePoints}
          paginationInfo={paginationInfo}
          totalItemsCount={allPointsCount}
          hasMultiselect={false}
          selectorRenderer={Checkbox}
        />
      )}

      {isEmpty && (
        <EmptyState>
          <span>{TEXTS.MODAL_BIND_POINTS_LIST_EMPTY}</span>
        </EmptyState>
      )}

      {isEmptyFiltered && (
        <EmptyState>
          <span>{TEXTS.MODAL_BIND_POINTS_FILTERED_LIST_EMPTY}</span>
        </EmptyState>
      )}
    </ModalScreen>
  );
};

export default memo(StreamBindModal);
